import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { confirmForgotPassword, forgotPassword, setNewPassword, signIn } from '../auth';
import { Logo } from '../components/Logo';
import { getConfig } from '../config';
import { flushSignupProfile } from '../public/SignUpPage';

type Mode = 'sign-in' | 'new-password' | 'forgot' | 'reset';

const titles: Record<Mode, string> = {
  'sign-in': 'Race director sign-in',
  'new-password': 'Choose a new password',
  forgot: 'Reset your password',
  reset: 'Reset your password',
};

export function LoginPage() {
  const navigate = useNavigate();
  const dev = getConfig().authMode === 'dev';
  const [mode, setMode] = useState<Mode>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [session, setSession] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [newPassword, setNewPasswordValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const go = (next: Mode) => {
    setMode(next);
    setError(null);
    setInfo(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (dev) {
        localStorage.setItem('splitline.devUser', email || 'dev-user');
        navigate('/');
      } else if (mode === 'new-password') {
        await setNewPassword(email, newPassword, session!);
        navigate('/');
      } else if (mode === 'forgot') {
        await forgotPassword(email);
        setMode('reset');
        setInfo(`If ${email} has a Splitline login, a code is on its way. Check your inbox (and spam).`);
      } else if (mode === 'reset') {
        await confirmForgotPassword(email, code.trim(), newPassword);
        setInfo('Password changed. Sign in with your new password.');
        setPassword('');
        setNewPasswordValue('');
        setCode('');
        setMode('sign-in');
      } else {
        let res;
        try {
          res = await signIn(email, password);
        } catch (err) {
          // Signed up but never entered the emailed code.
          if ((err as { code?: string }).code === 'UserNotConfirmedException') {
            navigate(`/signup?confirm=${encodeURIComponent(email)}`);
            return;
          }
          throw err;
        }
        if (res.kind === 'new-password') {
          setSession(res.session);
          setMode('new-password');
        } else {
          await flushSignupProfile().catch(() => undefined);
          navigate('/');
        }
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const choosing = mode === 'new-password' || mode === 'reset';

  return (
    <div className="center-page">
      <form className="card center-card stack" onSubmit={submit}>
        <div className="inline" style={{ gap: 10 }}>
          <Logo />
          <span className="display" style={{ fontSize: 26 }}>
            Splitline
          </span>
        </div>
        <h1 className="display" style={{ fontSize: 22 }}>
          {titles[mode]}
        </h1>
        {dev && <div className="notice info">Local dev mode: any name signs you in.</div>}
        {info && <div className="notice info">{info}</div>}
        <label className="field">
          Email
          <input className="input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required={!dev} disabled={choosing} />
        </label>
        {!dev && mode === 'sign-in' && (
          <label className="field">
            Password
            <input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </label>
        )}
        {mode === 'reset' && (
          <label className="field">
            Code from the email
            <input className="input mono" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required />
          </label>
        )}
        {choosing && (
          <label className="field">
            New password
            <input className="input" type="password" autoComplete="new-password" minLength={12} value={newPassword} onChange={(e) => setNewPasswordValue(e.target.value)} required />
            <span className="hint">At least 12 characters, with upper and lower case letters, a number and a symbol.</span>
          </label>
        )}
        {error && <div className="notice bad">{error}</div>}
        <button className="btn primary" type="submit" disabled={busy}>
          {mode === 'sign-in' ? 'Sign in' : mode === 'forgot' ? 'Email me a code' : mode === 'reset' ? 'Change password' : 'Save and sign in'}
        </button>
        {!dev && mode === 'sign-in' && (
          <button type="button" className="btn link" onClick={() => go('forgot')}>
            Forgot password?
          </button>
        )}
        {(mode === 'forgot' || mode === 'reset') && (
          <button type="button" className="btn link" onClick={() => go('sign-in')}>
            Back to sign-in
          </button>
        )}
        {mode === 'sign-in' && !dev && (
          <p className="small" style={{ margin: 0 }}>
            New to Splitline? <Link to="/signup">Create a free account</Link>
          </p>
        )}
        <p className="small muted" style={{ margin: 0 }}>
          Volunteer with a camera phone? <a href="/station">Pair your phone here.</a>
        </p>
      </form>
    </div>
  );
}
